import { api, ErrorApi, type ModoPago } from '../api';
import { identificador } from '../identificador';
import { informarDeFallo } from './telemetria';
import {
  ALMACEN_ACCIONES,
  ALMACEN_LOCALES,
  ALMACEN_REFERENCIAS,
  borrar,
  guardar,
  obtener,
  todos,
} from './db';

/**
 * Cola de acciones pendientes.
 *
 * El caso que resuelve: estás en el recibidor de un cliente sin cobertura,
 * creas la nota, metes tres tickets y te vas. Todo se guarda en el móvil y sube
 * solo cuando vuelve la red.
 *
 * Lo que lo hace no trivial es que las acciones **dependen unas de otras**: la
 * foto necesita el id del gasto, y el gasto el de la nota, y esos ids los da el
 * servidor. Por eso cada acción que crea algo publica una *referencia local*
 * (`n:uuid`, `g:uuid`) que las siguientes usan como marcador, y al completarse
 * se apunta a qué id real corresponde. Al enviar, los marcadores se sustituyen.
 *
 * El orden es FIFO estricto y una acción bloqueada detiene la cola: adelantar
 * la foto al gasto solo conseguiría que fallara.
 */

export type TipoAccion =
  | 'crear-nota'
  | 'crear-gasto'
  | 'subir-foto'
  | 'enviar-nota'
  | 'borrar-nota';

export type EstadoAccion = 'pendiente' | 'enviando' | 'error';

export interface Accion {
  /** Vale también como Idempotency-Key: se genera al encolar, no al enviar. */
  id: string;
  tipo: TipoAccion;
  /** Referencia local que esta acción resuelve al completarse, si crea algo. */
  produce?: string;
  datos: Record<string, unknown>;
  estado: EstadoAccion;
  intentos: number;
  ultimoError?: string;
  /** Un error de validación no se arregla reintentando: necesita al usuario. */
  irrecuperable?: boolean;
  /**
   * Orden estricto de la cola.
   *
   * NO se usa la marca de tiempo para ordenar: crear la nota, el gasto y la
   * foto ocurre en el mismo milisegundo, y con marcas empatadas el desempate lo
   * acababa haciendo el id (un UUID aleatorio). Eso mandaba a veces la foto
   * antes que su gasto, el id del padre aún no existía y la cola se quedaba
   * atascada — justo en el escenario para el que existe la app.
   */
  orden: number;
  creadoEn: number;
  /**
   * Momento a partir del cual tiene sentido reintentar.
   *
   * Sin esto, cada vuelta de la app a primer plano relanzaba la cola entera
   * contra un servidor que puede llevar caído un rato, gastando batería y datos
   * del LM para nada.
   */
  proximoIntento?: number;
}

/** Referencia local -> id real de Gextia, una vez subido. */
interface Referencia {
  idLocal: string;
  idServidor: number;
}

/**
 * Copia local de lo que aún no existe en Gextia, para poder pintarlo.
 *
 * Sin esto la nota que acabas de crear sin cobertura desaparecería de la
 * pantalla hasta que hubiera red, que es exactamente la sensación de "esto no
 * ha guardado" que queremos evitar.
 */
export interface EntidadLocal {
  idLocal: string;
  tipo: 'nota' | 'gasto' | 'foto';
  /** Referencia local o id numérico del padre (nota de un gasto, gasto de una foto). */
  padre: string;
  datos: Record<string, unknown>;
}

type Escucha = () => void;
const escuchas = new Set<Escucha>();

function avisar(): void {
  for (const escucha of escuchas) escucha();
}

export function suscribir(escucha: Escucha): () => void {
  escuchas.add(escucha);
  return () => escuchas.delete(escucha);
}

export function nuevaReferencia(prefijo: 'n' | 'g' | 'f'): string {
  return `${prefijo}:${identificador()}`;
}

export function esReferenciaLocal(valor: unknown): valor is string {
  return typeof valor === 'string' && /^[ngf]:/.test(valor);
}

// --------------------------------------------------------------------- encolar

/**
 * Contador de orden. Se inicializa una vez desde lo que ya hubiera en cola
 * (para que un reinicio de la app no reordene lo pendiente) y a partir de ahí
 * crece en memoria, sin depender del reloj.
 */
let ultimoOrden: number | null = null;

async function siguienteOrden(): Promise<number> {
  if (ultimoOrden === null) {
    const existentes = await todos<Accion>(ALMACEN_ACCIONES);
    ultimoOrden = existentes.reduce((maximo, a) => Math.max(maximo, a.orden ?? 0), 0);
  }
  ultimoOrden += 1;
  return ultimoOrden;
}

async function encolar(
  accion: Omit<Accion, 'estado' | 'intentos' | 'creadoEn' | 'orden'>,
): Promise<void> {
  await guardar<Accion>(ALMACEN_ACCIONES, {
    ...accion,
    estado: 'pendiente',
    intentos: 0,
    orden: await siguienteOrden(),
    creadoEn: Date.now(),
  });
  avisar();
  void procesar();
}

export async function encolarNota(nombre: string): Promise<string> {
  const ref = nuevaReferencia('n');
  await guardar<EntidadLocal>(ALMACEN_LOCALES, {
    idLocal: ref,
    tipo: 'nota',
    padre: '',
    datos: { name: nombre, totalAmount: 0, paymentMode: null, expenseIds: [], state: 'draft' },
  });
  await encolar({
    id: identificador(),
    tipo: 'crear-nota',
    produce: ref,
    datos: { nombre },
  });
  return ref;
}

export async function encolarGasto(
  notaRef: string | number,
  datos: {
    concepto: string;
    importe: number;
    categoriaId: number;
    categoriaNombre: string;
    fecha: string;
    modoPago: ModoPago;
  },
): Promise<string> {
  const ref = nuevaReferencia('g');
  await guardar<EntidadLocal>(ALMACEN_LOCALES, {
    idLocal: ref,
    tipo: 'gasto',
    padre: String(notaRef),
    datos: {
      description: datos.concepto,
      amount: datos.importe,
      date: datos.fecha,
      categoryId: datos.categoriaId,
      categoryName: datos.categoriaNombre,
      paymentMode: datos.modoPago,
      photoCount: 0,
    },
  });
  await encolar({
    id: identificador(),
    tipo: 'crear-gasto',
    produce: ref,
    datos: { notaId: notaRef, ...datos },
  });
  return ref;
}

export async function encolarFoto(
  gastoRef: string | number,
  archivo: Blob,
  nombre: string,
): Promise<string> {
  const ref = nuevaReferencia('f');
  await guardar<EntidadLocal>(ALMACEN_LOCALES, {
    idLocal: ref,
    tipo: 'foto',
    padre: String(gastoRef),
    datos: { filename: nombre, sizeBytes: archivo.size, blob: archivo },
  });
  await encolar({
    id: identificador(),
    tipo: 'subir-foto',
    produce: ref,
    datos: { gastoId: gastoRef, archivo, nombre },
  });
  return ref;
}

export async function encolarEnvio(notaRef: string | number): Promise<void> {
  await encolar({
    id: identificador(),
    tipo: 'enviar-nota',
    datos: { notaId: notaRef },
  });
}

/**
 * Borra una nota, y de paso todo lo que estuviera esperando para ella.
 *
 * Lo segundo importa tanto como lo primero: si quedaran en la cola la foto o el
 * gasto de una nota que se acaba de borrar, se pasarían la vida reintentando
 * contra un padre que ya no existe. Se limpian ANTES de encolar el borrado
 * para que no puedan colarse entremedias.
 *
 * Si la nota nunca llegó a subirse —se creó sin cobertura y aún tiene
 * referencia local—, no hay nada que borrar en Gextia: basta con quitarla de
 * aquí.
 */
export async function encolarBorradoDeNota(notaRef: string | number): Promise<void> {
  const subida = await olvidarLoPendienteDe(notaRef);
  if (!subida) return;

  await encolar({
    id: identificador(),
    tipo: 'borrar-nota',
    datos: { notaId: notaRef },
  });
}

/**
 * Quita de la cola y del almacén local todo lo que cuelgue de una nota.
 *
 * Devuelve si la nota llegó a existir en Gextia, que es lo que decide si hay
 * algo que borrar allí o solo aquí.
 */
async function olvidarLoPendienteDe(notaRef: string | number): Promise<boolean> {
  const ref = String(notaRef);
  const locales = await todos<EntidadLocal>(ALMACEN_LOCALES);

  // Los gastos cuelgan de la nota y las fotos de los gastos: hay que bajar dos
  // niveles, no uno.
  const gastos = locales.filter((e) => e.tipo === 'gasto' && e.padre === ref);
  const refsGasto = new Set(gastos.map((g) => g.idLocal));
  const fotos = locales.filter((e) => e.tipo === 'foto' && refsGasto.has(e.padre));
  const afectadas = new Set<string>([ref, ...refsGasto, ...fotos.map((f) => f.idLocal)]);

  for (const accion of await todos<Accion>(ALMACEN_ACCIONES)) {
    const tocaLaNota =
      (accion.produce && afectadas.has(accion.produce)) ||
      afectadas.has(String(accion.datos.notaId ?? '')) ||
      afectadas.has(String(accion.datos.gastoId ?? ''));
    if (tocaLaNota) await borrar(ALMACEN_ACCIONES, accion.id);
  }

  for (const idLocal of afectadas) {
    await borrar(ALMACEN_LOCALES, idLocal);
  }

  // Si la referencia es un número, la nota ya vive en Gextia. Si es local pero
  // tiene traducción a id de servidor, también: se subió y aún no se limpió.
  if (typeof notaRef === 'number') return true;
  const referencia = await obtener<Referencia>(ALMACEN_REFERENCIAS, ref);
  return referencia !== undefined;
}

// -------------------------------------------------------------------- consulta

export function accionesPendientes(): Promise<Accion[]> {
  return todos<Accion>(ALMACEN_ACCIONES);
}

export function entidadesLocales(): Promise<EntidadLocal[]> {
  return todos<EntidadLocal>(ALMACEN_LOCALES);
}

/** Descarta una acción atascada y lo que colgaba de ella. */
export async function descartar(accionId: string): Promise<void> {
  const accion = await obtener<Accion>(ALMACEN_ACCIONES, accionId);
  await borrar(ALMACEN_ACCIONES, accionId);
  if (accion?.produce) {
    await borrar(ALMACEN_LOCALES, accion.produce);
  }
  avisar();
}

// ------------------------------------------------------------------ procesado

async function resolver(valor: unknown): Promise<number | null> {
  if (typeof valor === 'number') return valor;
  if (!esReferenciaLocal(valor)) return null;
  const referencia = await obtener<Referencia>(ALMACEN_REFERENCIAS, valor);
  return referencia?.idServidor ?? null;
}

let enCurso: Promise<void> | null = null;
let hayQueRepetir = false;

/**
 * La cola está parada porque la API key de Gextia ya no vale.
 *
 * No es un fallo de las acciones: en cuanto el usuario vuelva a conectar su
 * cuenta, todas siguen siendo válidas. Marcarlas como irrecuperables —que es lo
 * que hacía antes— habría borrado del mapa las fotos ya hechas de un LM al que
 * simplemente le rotaron la clave.
 */
let pausadaPorClave = false;

export function colaPausadaPorClave(): boolean {
  return pausadaPorClave;
}

/** Se llama tras reconectar la cuenta de Gextia. */
export function reanudarTrasReconectar(): Promise<void> {
  pausadaPorClave = false;
  return procesar({ forzar: true });
}

/** Espera creciente entre reintentos, con techo para que nunca se rinda del todo. */
function esperaTras(intentos: number): number {
  const TOPE = 15 * 60 * 1000;
  return Math.min(30_000 * 2 ** Math.max(0, intentos - 1), TOPE);
}

/**
 * Vacía la cola. Seguro de llamar desde cualquier sitio y en cualquier momento.
 *
 * Si ya se está ejecutando, devuelve la pasada en curso en vez de no hacer nada:
 * así `await procesar()` significa de verdad "espera a que termine". Y si
 * durante la pasada se encoló algo nuevo, se da otra vuelta al terminar, para
 * que una foto hecha mientras subía la anterior no se quede esperando al
 * siguiente disparador.
 */
export function procesar(opciones: { forzar?: boolean } = {}): Promise<void> {
  if (enCurso) {
    hayQueRepetir = true;
    return enCurso;
  }

  const forzar = opciones.forzar === true;

  enCurso = (async () => {
    try {
      do {
        hayQueRepetir = false;
        await unaPasada(forzar);
      } while (hayQueRepetir);
    } finally {
      enCurso = null;
      avisar();
    }
  })();

  return enCurso;
}

async function unaPasada(forzar: boolean): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (pausadaPorClave && !forzar) return;

  const ahora = Date.now();
  const acciones = (await accionesPendientes())
    .filter((a) => !a.irrecuperable)
    .sort((a, b) => a.orden - b.orden);

  for (const accion of acciones) {
    // La espera se respeta parando, no saltando: adelantar la foto a su gasto
    // solo conseguiría que fallara por falta de padre.
    if (!forzar && accion.proximoIntento !== undefined && accion.proximoIntento > ahora) {
      break;
    }

    const seguir = await procesarUna(accion);
    // Un fallo de red detiene la cola entera: si el servidor no está, las
    // siguientes también fallarían y solo gastaríamos batería y datos.
    if (!seguir) break;
  }
}

async function procesarUna(accion: Accion): Promise<boolean> {
  await guardar<Accion>(ALMACEN_ACCIONES, { ...accion, estado: 'enviando' });
  avisar();

  try {
    const idServidor = await ejecutar(accion);

    if (accion.produce && idServidor !== null) {
      await guardar<Referencia>(ALMACEN_REFERENCIAS, {
        idLocal: accion.produce,
        idServidor,
      });
      await borrar(ALMACEN_LOCALES, accion.produce);
    }
    await borrar(ALMACEN_ACCIONES, accion.id);
    avisar();
    return true;
  } catch (error) {
    const esApi = error instanceof ErrorApi;
    const codigo = esApi ? error.codigo : null;
    const intentos = accion.intentos + 1;

    // La clave revocada no es culpa de la acción: en cuanto el usuario reconecte
    // su cuenta, todas siguen valiendo. Se para la cola y se deja intacto lo
    // encolado — marcarlo como irrecuperable borraría las fotos ya hechas.
    if (codigo === 'GEXTIA_KEY_INVALIDA') {
      pausadaPorClave = true;
      await guardar<Accion>(ALMACEN_ACCIONES, {
        ...accion,
        estado: 'pendiente',
        intentos,
        ultimoError: 'Tu cuenta de Gextia necesita volver a conectarse.',
      });
      avisar();
      return false;
    }

    // Alguien lo borró en Gextia mientras esto esperaba en la cola. Insistir no
    // lo va a resucitar, y dejarlo como error irrecuperable llenaría la
    // pantalla de pendientes de fallos que el usuario no puede arreglar. Se
    // limpia todo lo que colgaba de ello y se sigue.
    //
    // Si la acción era precisamente borrar la nota, el resultado que se buscaba
    // ya se ha cumplido: la nota no está.
    if (codigo === 'YA_NO_EXISTE') {
      await borrar(ALMACEN_ACCIONES, accion.id);
      const refNota = accion.datos.notaId;
      if (refNota !== undefined) await olvidarLoPendienteDe(refNota as string | number);
      avisar();
      return true;
    }

    const reintentable = !esApi || error.reintentable;

    await guardar<Accion>(ALMACEN_ACCIONES, {
      ...accion,
      estado: 'error',
      intentos,
      ultimoError: error instanceof Error ? error.message : 'Error desconocido',
      // Una validación o una regla de Gextia no se arregla insistiendo. Se marca
      // para que el usuario decida, en vez de reintentar en bucle para siempre.
      irrecuperable: esApi && !error.reintentable,
      proximoIntento: reintentable ? Date.now() + esperaTras(intentos) : undefined,
    });
    avisar();
    void informarDeFallo(accion.tipo, codigo, intentos);
    return reintentable ? false : true;
  }
}

/** Ejecuta una acción y devuelve el id que asignó Gextia, si crea algo. */
async function ejecutar(accion: Accion): Promise<number | null> {
  switch (accion.tipo) {
    case 'crear-nota': {
      const nota = await api.crearNota(accion.datos.nombre as string, accion.id);
      return nota.id;
    }

    case 'crear-gasto': {
      const notaId = await resolver(accion.datos.notaId);
      if (notaId === null) {
        throw new ErrorApi('Esperando a que suba la nota.', 'SIN_RED', true);
      }
      const gasto = await api.crearGasto(
        notaId,
        {
          concepto: accion.datos.concepto as string,
          importe: accion.datos.importe as number,
          categoriaId: accion.datos.categoriaId as number,
          fecha: accion.datos.fecha as string,
          modoPago: accion.datos.modoPago as ModoPago,
        },
        accion.id,
      );
      return gasto.id;
    }

    case 'subir-foto': {
      const gastoId = await resolver(accion.datos.gastoId);
      if (gastoId === null) {
        throw new ErrorApi('Esperando a que suba el gasto.', 'SIN_RED', true);
      }
      const blob = accion.datos.archivo as Blob;
      const archivo = new File([blob], accion.datos.nombre as string, {
        type: blob.type || 'image/jpeg',
      });
      const foto = await api.subirFoto(gastoId, archivo, accion.id);
      return foto.id;
    }

    case 'borrar-nota': {
      const notaId = await resolver(accion.datos.notaId);
      // Sin traducción a id de servidor la nota nunca subió: no hay nada que
      // borrar allí, y darlo por hecho es lo correcto.
      if (notaId === null) return null;
      await api.borrarNota(notaId, accion.id);
      return null;
    }

    case 'enviar-nota': {
      const notaId = await resolver(accion.datos.notaId);
      if (notaId === null) {
        throw new ErrorApi('Esperando a que suba la nota.', 'SIN_RED', true);
      }

      // Guardia contra enviar una nota a medias.
      //
      // El orden FIFO ya pone el envío el último, pero una acción anterior
      // marcada como irrecuperable se salta, y entonces el envío saldría igual
      // y la nota llegaría a aprobación sin uno de sus gastos o sin la foto del
      // ticket. Quien aprueba no tiene forma de saber que falta algo.
      const anteriores = (await accionesPendientes()).filter((a) => a.orden < accion.orden);
      if (anteriores.length > 0) {
        const atascadas = anteriores.filter((a) => a.irrecuperable);
        if (atascadas.length > 0) {
          throw new ErrorApi(
            'No se envía porque queda algo sin subir de esta nota. Revísalo antes.',
            'VALIDACION',
            false,
          );
        }
        throw new ErrorApi('Esperando a que suba el resto de la nota.', 'SIN_RED', true);
      }

      await api.enviarNota(notaId, accion.id);
      return null;
    }
  }
}

/** Traduce una referencia local a su id real, si ya subió. */
export async function idRealDe(referencia: string): Promise<number | null> {
  return resolver(referencia);
}

// ------------------------------------------------------------------ disparadores

if (typeof window !== 'undefined') {
  // Recuperar la red es información nueva: se salta la espera creciente, que
  // está pensada para no machacar a un servidor que sigue caído, no para
  // castigar al usuario que acaba de salir del ascensor.
  window.addEventListener('online', () => {
    void procesar({ forzar: true });
  });

  // Volver a primer plano NO fuerza: si el servidor está caído, abrir la app
  // cada dos minutos no debe reintentarlo cada dos minutos.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void procesar();
  });
}
